import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { ProductShowcase } from "../components/ProductShowcase";
import { ProductCard } from "./ShopPage";
import { useCatalog, normalise } from "../catalog";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const L = {
    en: { back: "Back to the Boutique", more: "You may also love", notFound: "This attar could not be found." },
    fr: { back: "Retour à la Boutique", more: "Vous aimerez aussi", notFound: "Cet attar est introuvable." },
    ar: { back: "العودة إلى المتجر", more: "قد يعجبك أيضاً", notFound: "لم يتم العثور على هذا العطر." },
    bn: { back: "বুটিকে ফিরে যান", more: "আপনার পছন্দ হতে পারে", notFound: "এই আতরটি পাওয়া যায়নি।" },
};

export default function ProductPage({ onAdd }) {
    const { slug } = useParams();
    const { products } = useCatalog();
    const { lang } = useLang();
    const l = L[lang] ?? L.en;
    const cached = products.find((p) => p.slug === slug || p.id === slug);
    const [product, setProduct] = useState(cached || null);
    const [missing, setMissing] = useState(false);

    useEffect(() => {
        if (cached) { setProduct(cached); return; }
        axios.get(`${API}/products/${slug}`).then(({ data }) => setProduct(normalise(data))).catch(() => setMissing(true));
    }, [slug, cached]);

    const related = products.filter((p) => p.id !== product?.id && p.category === product?.category).slice(0, 3);

    return (
        <div className="pt-20 sm:pt-24 bg-ivory min-h-screen" data-testid="product-page">
            <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-6">
                <Link to="/shop" data-testid="product-back-link" className="inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase text-golddark hover:text-charcoal transition-colors"><ArrowLeft size={12} /> {l.back}</Link>
            </div>
            {missing ? (
                <p className="font-serif text-2xl text-ash italic py-32 text-center" data-testid="product-not-found">{l.notFound}</p>
            ) : product ? (
                <>
                    <ProductShowcase product={product} index={0} onAdd={onAdd} />
                    {related.length > 0 && (
                        <section className="bg-cream py-24 px-6 sm:px-10" data-testid="product-related">
                            <div className="max-w-7xl mx-auto">
                                <h2 className="font-serif text-3xl text-charcoal mb-10">{l.more}</h2>
                                <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-12">
                                    {related.map((p, i) => <ProductCard key={p.id} p={p} i={i} onAdd={onAdd} l={{ soldOut: "Sold out", from: "from", view: "View", add: "Add" }} lang={lang} />)}
                                </div>
                            </div>
                        </section>
                    )}
                </>
            ) : (
                <div className="max-w-7xl mx-auto px-6 sm:px-10 py-24 grid lg:grid-cols-2 gap-16"><div className="aspect-[4/5] bg-cream animate-pulse" /><div className="space-y-6 pt-10"><div className="h-4 w-40 bg-cream animate-pulse" /><div className="h-12 w-2/3 bg-cream animate-pulse" /><div className="h-24 bg-cream animate-pulse" /></div></div>
            )}
        </div>
    );
}
